import moment from 'moment';
import type {
  ContactNoteCreateRequest,
  FollowUpEnum,
  MemberResponseEnum,
  ReasonEnum,
  StatusNeedEnum,
  T1016ActivityEnum,
} from 'api/v2';
import {
  FOLLOW_UP_WITH_CLIENT,
  NOT_REACHED,
  OTHER,
  OTHER_PLACE_OF_SERVICE,
} from './choices';
import {narrativeOf} from './rules';
import type {Checklist, ContactNoteState} from './state';
import {createInitialState} from './state';

type Field = keyof ContactNoteState;

const REACHED_FIELDS: readonly Field[] = [
  'serviceCode',
  'placeOfService',
  'placeOfServiceOther',
  'activities',
  'specificNeed',
  'startTime',
  'endTime',
  'serviceDescription',
  'reasons',
  'objectiveReason',
  'changeStatus',
  'needs',
  'relevantChange',
  'observation',
  'responses',
  'responseOutcome',
  'followUps',
  'followUpPlan',
  'followUpAt',
];

const NOT_REACHED_FIELDS: readonly Field[] = [
  'notReachedReason',
  'notReachedReasonOther',
  'nextAttemptAt',
  'notReachedNote',
];

const blanksFor = (fields: readonly Field[]): Partial<ContactNoteState> => {
  const initial = createInitialState();
  return Object.fromEntries(fields.map((field) => [field, initial[field]]));
};

const withoutUnusedOther = ({selected, other}: Checklist): Checklist => ({
  selected,
  other: selected.includes(OTHER) ? other : '',
});

// Hidden or unticked answers stay in the form so a mis-click can be undone, but are never saved
export const toPayload = (
  state: ContactNoteState,
): ContactNoteState & {narrative: string} => {
  const cleaned: ContactNoteState = {
    ...state,
    notReachedReasonOther:
      state.notReachedReason === OTHER ? state.notReachedReasonOther : '',
    placeOfServiceOther:
      state.placeOfService?.code === OTHER_PLACE_OF_SERVICE
        ? state.placeOfServiceOther
        : '',
    activities: withoutUnusedOther(state.activities),
    reasons: withoutUnusedOther(state.reasons),
    needs: withoutUnusedOther(state.needs),
    responses: withoutUnusedOther(state.responses),
    followUps: withoutUnusedOther(state.followUps),
    ...(state.followUps.selected.includes(FOLLOW_UP_WITH_CLIENT)
      ? {}
      : blanksFor(['followUpAt'])),
    ...blanksFor(
      state.contactMethod === NOT_REACHED ? REACHED_FIELDS : NOT_REACHED_FIELDS,
    ),
  };

  return {...cleaned, narrative: narrativeOf(cleaned)};
};

// The API requires a UTC offset on every timestamp
const withOffset = (value: string) => (value ? moment(value).format() : null);

// The cast is safe: checklist keys come from choices typed against the API enums
const withKeys = <K extends string>({selected, other}: Checklist) => ({
  selected: selected as K[],
  other,
});

// `...answers` skips TypeScript's excess key check, and the API rejects unknown keys
type WithoutExtraKeys<T, Shape> = T &
  Record<Exclude<keyof T, keyof Shape>, never>;

export const toRequest = (
  state: ContactNoteState,
): ContactNoteCreateRequest => {
  const {member, contactMethod, placeOfService, ...answers} = toPayload(state);
  if (!member || !contactMethod) {
    throw new Error(
      'Save is only enabled once member and contact method are set',
    );
  }
  const onServiceDate = (time: string) =>
    withOffset(time && `${answers.dateOfService}T${time}`);

  const request = {
    ...answers,
    member: member.id,
    contactMethod,
    placeOfServiceCode: placeOfService?.code ?? null,
    nextAttemptAt: withOffset(answers.nextAttemptAt),
    startTime: onServiceDate(answers.startTime),
    endTime: onServiceDate(answers.endTime),
    followUpAt: withOffset(answers.followUpAt),
    activities: withKeys<T1016ActivityEnum>(answers.activities),
    reasons: withKeys<ReasonEnum>(answers.reasons),
    needs: withKeys<StatusNeedEnum>(answers.needs),
    responses: withKeys<MemberResponseEnum>(answers.responses),
    followUps: withKeys<FollowUpEnum>(answers.followUps),
  };
  return request satisfies WithoutExtraKeys<
    typeof request,
    ContactNoteCreateRequest
  >;
};
